import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  TrendUp, TrendDown, Warning, Target, Fire, Eye, Pause, Sparkle,
  Question, ShoppingBag, Lightning, ChartLineUp, ImageSquare,
  CurrencyInr, Package, ListBullets, CaretRight, CaretDown,
  ArrowSquareOut, FacebookLogo, PlayCircle, Image as ImageIcon,
  Play, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import PerformanceIntelligencePanel from "./PerformanceIntelligencePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- formatting ----------
const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const fmtMoney = (n, c = "INR") => {
  if (!n && n !== 0) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${c} ${fmtNum(n)}`;
  }
};
const fmtPct = (n) => (n === undefined || n === null ? "-" : `${Number(n).toFixed(2)}%`);
const fmtRoas = (n) => (n ? `${Number(n).toFixed(2)}x` : "-");

// ---------- market detection ----------
// Best-effort parse of campaign name. Returns {market, funnel}
const MARKET_PATTERNS = [
  { re: /\b(?:dom|domestic|india|in)[ _\-\|]/i, market: "Domestic" },
  { re: /\b(?:int|intl|international|row)[ _\-\|]/i, market: "International" },
  { re: /\b(?:usa|us|united[ _]states)\b/i, market: "USA" },
  { re: /\b(?:uk|united[ _]kingdom|britain)\b/i, market: "UK" },
  { re: /\b(?:uae|dubai)\b/i, market: "UAE" },
  { re: /\b(?:aus|australia)\b/i, market: "Australia" },
  { re: /\b(?:canada|ca)\b/i, market: "Canada" },
  { re: /\b(?:sg|singapore)\b/i, market: "Singapore" },
];
const FUNNEL_PATTERNS = [
  { re: /\b(?:rmkt|rem|remarketing|retargeting|retarget)\b/i, funnel: "Retargeting" },
  { re: /\b(?:con|conversion|conversions)\b/i, funnel: "Conversion" },
  { re: /\b(?:tof|top[ _]of[ _]funnel|cold)\b/i, funnel: "TOF / Cold" },
  { re: /\b(?:prospect|prospecting)\b/i, funnel: "Prospecting" },
  { re: /\b(?:prebuzz|teaser)\b/i, funnel: "Prebuzz" },
  { re: /\b(?:sale|eoss|clearance)\b/i, funnel: "Sale / EOSS" },
];

const detectMarketFunnel = (campaignName = "") => {
  let market = null;
  let funnel = null;
  for (const p of MARKET_PATTERNS) {
    if (p.re.test(campaignName)) { market = p.market; break; }
  }
  for (const p of FUNNEL_PATTERNS) {
    if (p.re.test(campaignName)) { funnel = p.funnel; break; }
  }
  return { market, funnel };
};

// ---------- performance labeling ----------
// Defaults approved by user (1a). These can be made per-client in Phase 4.
const DEFAULT_THRESHOLDS = {
  scaleRoas: 2.5,
  scaleMinOrders: 1,
  scaleMinSpend: 500,
  watchMinCtr: 1.5,
  pauseMinSpend: 2000,
  pauseMaxRoas: 0.5,
  winnerCtr: 2.0,
  winnerMaxCpc: 15,
  trackingMissingMinPurchases: 1,
};

const labelAd = (ad, ctx, thr = DEFAULT_THRESHOLDS) => {
  const m = ad.metrics || {};
  const sh = ad.shopify || {};
  const spend = m.spend || 0;
  const ctr = m.ctr || 0;
  const cpc = m.cpc || 0;
  const purchases = m.purchases || 0;
  const shOrders = sh.orders || 0;
  const shRevenue = sh.revenue || 0;
  const roas = sh.roas || 0;
  const hasProduct = !!(sh.product || sh.collection);
  const labels = [];

  // Primary status (exactly one of the 5 status labels)
  let primary = null;
  if (roas >= thr.scaleRoas && shOrders >= thr.scaleMinOrders && spend >= thr.scaleMinSpend) {
    primary = "scaling";
  } else if (spend >= thr.pauseMinSpend && (roas < thr.pauseMaxRoas || (shOrders === 0 && purchases === 0))) {
    primary = "needs_attention";
  } else if (ctr >= thr.watchMinCtr && (shOrders === 0 || roas < 1) && spend >= 200) {
    primary = "monitor";
  } else if (spend < thr.scaleMinSpend && spend > 0) {
    primary = "learning";
  } else if (spend === 0 || (shOrders === 0 && purchases === 0 && spend < thr.pauseMinSpend)) {
    primary = "no_traction";
  }
  if (primary) labels.push(primary);

  // Secondary flags (can co-exist)
  if (purchases >= thr.trackingMissingMinPurchases && shOrders === 0 && hasProduct) {
    labels.push("tracking_missing");
  }
  if (spend > 0 && !hasProduct) {
    labels.push("needs_mapping");
  }
  if (ctr >= thr.winnerCtr && cpc > 0 && cpc <= thr.winnerMaxCpc) {
    labels.push("creative_winner");
  }
  if (ctx?.topProductIds && sh.product?.id && ctx.topProductIds.has(sh.product.id)) {
    labels.push("product_winner");
  }

  return labels;
};

const LABEL_META = {
  scaling:          { bg: "#16A34A", color: "#FFFFFF", icon: TrendUp,      text: "Scaling" },
  monitor:          { bg: "#EAB308", color: "#111111", icon: Eye,          text: "Monitor" },
  needs_attention:  { bg: "#DC2626", color: "#FFFFFF", icon: Warning,      text: "Needs Attention" },
  learning:         { bg: "#3B82F6", color: "#FFFFFF", icon: Lightning,    text: "Learning" },
  no_traction:      { bg: "#52525B", color: "#FFFFFF", icon: Question,     text: "No Clear Traction" },
  creative_winner:  { bg: "#7C3AED", color: "#FFFFFF", icon: Sparkle,      text: "Creative Winner" },
  product_winner:   { bg: "#0EA5E9", color: "#FFFFFF", icon: ShoppingBag,  text: "Product Winner" },
  tracking_missing: { bg: "#F97316", color: "#FFFFFF", icon: Warning,      text: "Tracking Mismatch" },
  needs_mapping:    { bg: "#A855F7", color: "#FFFFFF", icon: Question,     text: "Needs Mapping" },
};

const LabelPill = ({ l, compact = false }) => {
  const m = LABEL_META[l];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded font-semibold ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"}`}
      style={{ background: m.bg, color: m.color }}
      data-testid={`label-${l}`}
    >
      <Icon size={compact ? 9 : 10} weight="bold" />
      {m.text}
    </span>
  );
};

// ---------- KPI tile ----------
const Kpi = ({ label, value, sub, color, icon: Icon }) => (
  <div className="card-flat p-3 flex items-start justify-between gap-2" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="min-w-0">
      <div className="small-label">{label}</div>
      <div className="data-number text-lg mt-1 truncate" style={color ? { color } : {}}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-[#71717A] mt-0.5 truncate">{sub}</div>}
    </div>
    {Icon && <Icon size={18} className="text-[#52525B] shrink-0" />}
  </div>
);

// ---------- creative card ----------
const AdThumb = ({ url, format, size = 180 }) => {
  const [err, setErr] = useState(false);
  if (url && !err) {
    return (
      <div className="relative w-full overflow-hidden bg-[#0A0A0C]" style={{ aspectRatio: "1/1", maxHeight: size }}>
        <img
          src={url}
          alt=""
          onError={() => setErr(true)}
          className="w-full h-full object-cover"
        />
        {(format === "video" || format === "reel") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
            <PlayCircle size={40} weight="fill" className="text-white drop-shadow-lg" />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="w-full bg-[#1F1F22] flex items-center justify-center" style={{ aspectRatio: "1/1", maxHeight: size }}>
      <ImageIcon size={48} className="text-[#52525B]" />
    </div>
  );
};

const CreativeCard = ({ ad, currency, metaUrl, onPickProduct, clientId, onAction }) => {
  const cr = ad.creative || {};
  const m = ad.metrics || {};
  const sh = ad.shopify || {};
  const product = sh.product;
  const coll = sh.collection;
  const spend = m.spend || 0;
  const shRevenue = sh.revenue || 0;
  const shOrders = sh.orders || 0;
  const roas = sh.roas || 0;
  const labels = ad._labels || [];
  const thumb = cr.thumbnail_url || ad.thumbnail_url;
  const fmt = cr.format || ad.creative_type;
  const dest = cr.destination_url;

  return (
    <div className="card-flat overflow-hidden flex flex-col" data-testid="creative-card">
      <AdThumb url={thumb} format={fmt} />
      {labels.length > 0 && (
        <div className="p-2 flex flex-wrap gap-1 border-b border-[#1F1F22]">
          {labels.slice(0, 3).map((l) => <LabelPill key={l} l={l} compact />)}
        </div>
      )}
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div>
          <div className="text-xs font-semibold text-[#E4E4E7] line-clamp-2" title={ad.name}>
            {ad.name || "(unnamed)"}
          </div>
          {cr.title && (
            <div className="text-[10px] text-[#A1A1AA] line-clamp-1 mt-1" title={cr.title}>
              {cr.title}
            </div>
          )}
        </div>

        <div className="text-[10px] text-[#71717A] space-y-0.5">
          <div className="truncate" title={ad._campaign_name}>📁 {ad._campaign_name || "—"}</div>
          <div className="truncate" title={ad._adset_name}>🎯 {ad._adset_name || "—"}</div>
        </div>

        {/* Product / Collection chip */}
        {product ? (
          <div className="flex items-center gap-2 bg-[#10B98111] border border-[#10B98133] rounded p-1.5">
            {product.image ? (
              <img src={product.image} alt="" className="w-8 h-8 rounded object-cover" />
            ) : (
              <div className="w-8 h-8 rounded bg-[#1F1F22] flex items-center justify-center"><Package size={14} className="text-[#52525B]" /></div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-[#10B981] font-semibold truncate" title={product.title}>{product.title}</div>
              <div className="text-[9px] text-[#52525B] truncate">/{product.handle}</div>
            </div>
          </div>
        ) : coll ? (
          <div className="flex items-center gap-2 bg-[#FCD34D11] border border-[#FCD34D33] rounded p-1.5">
            {coll.image ? (
              <img src={coll.image} alt="" className="w-8 h-8 rounded object-cover" />
            ) : (
              <div className="w-8 h-8 rounded bg-[#1F1F22] flex items-center justify-center"><ListBullets size={14} className="text-[#52525B]" /></div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-[#FCD34D] font-semibold truncate" title={coll.title}>Collection: {coll.title}</div>
              <div className="text-[9px] text-[#52525B] truncate">{coll.products_count || 0} products</div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => onPickProduct(ad.id)}
            className="text-[10px] text-[#71717A] hover:text-[#FFEB00] border border-dashed border-[#27272A] rounded p-1.5 text-left"
            data-testid="tag-product-btn"
          >
            + Tag a Shopify product
          </button>
        )}

        {/* Metrics grid */}
        <div className="grid grid-cols-3 gap-1.5 text-[10px] mt-1">
          <Metric label="Spend" value={fmtMoney(spend, currency)} accent="#FFEB00" />
          <Metric label="Revenue" value={shRevenue > 0 ? fmtMoney(shRevenue, currency) : "—"} accent={shRevenue > 0 ? "#86EFAC" : undefined} />
          <Metric
            label="ROAS"
            value={fmtRoas(roas)}
            accent={roas >= 2.5 ? "#86EFAC" : roas >= 1 ? "#FCD34D" : roas > 0 ? "#FF9999" : undefined}
          />
          <Metric label="CTR" value={fmtPct(m.ctr)} />
          <Metric label="CPC" value={fmtMoney(m.cpc, currency)} />
          <Metric label="Orders" value={fmtNum(shOrders)} />
          <Metric label="Meta Pur" value={fmtNum(m.purchases)} />
          <Metric label="ATC" value={fmtNum(m.add_to_cart)} />
          <Metric label="Impr" value={fmtNum(m.impressions)} />
        </div>

        <div className="flex items-center gap-2 pt-2 mt-auto border-t border-[#1F1F22]">
          <a
            href={metaUrl("ad", ad.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-[#1877F2] hover:underline inline-flex items-center gap-1"
          >
            <FacebookLogo size={11} weight="fill" /> Meta
          </a>
          {dest && (
            <a
              href={dest}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-[#A1A1AA] hover:text-[#FFEB00] inline-flex items-center gap-1"
            >
              <ArrowSquareOut size={11} /> Landing
            </a>
          )}
          {onAction && (
            ad.effective_status === "ACTIVE" ? (
              <button
                onClick={() => onAction({ kind: "pause_ad", entity_id: ad.id, entity_name: ad.name, level: "ad", current: ad })}
                className="text-[10px] text-[#FF9999] hover:text-[#FF6666] inline-flex items-center gap-1"
                title="Pause this ad on Meta"
                data-testid="pause-ad-btn"
              >
                <Pause size={11} weight="fill" /> Pause
              </button>
            ) : (
              <button
                onClick={() => onAction({ kind: "resume_ad", entity_id: ad.id, entity_name: ad.name, level: "ad", current: ad })}
                className="text-[10px] text-[#86EFAC] hover:text-[#10B981] inline-flex items-center gap-1"
                title="Resume this ad on Meta"
                data-testid="resume-ad-btn"
              >
                <Play size={11} weight="fill" /> Resume
              </button>
            )
          )}
          {onAction && ad._adset_id && (labels.includes("scaling") || labels.includes("creative_winner")) && (
            <button
              onClick={() => onAction({ kind: "adjust_adset_budget", entity_id: ad._adset_id, entity_name: ad._adset_name, level: "adset", current: ad })}
              className="text-[10px] text-[#FCD34D] hover:text-[#F59E0B] inline-flex items-center gap-1"
              title="Adjust this ad set's budget"
              data-testid="adjust-budget-btn"
            >
              <TrendUp size={11} weight="fill" /> Budget
            </button>
          )}
          <button
            onClick={() => onPickProduct(ad.id)}
            className="text-[10px] text-[#71717A] hover:text-[#FFEB00] ml-auto"
          >
            {product || coll ? "Remap" : "Tag"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Metric = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0C] rounded p-1.5">
    <div className="text-[8px] uppercase tracking-wide text-[#52525B]">{label}</div>
    <div className="text-[11px] data-number truncate" style={accent ? { color: accent } : {}}>{value}</div>
  </div>
);

// ---------- Section wrapper ----------
const Section = ({ title, subtitle, icon: Icon, color, children, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-flat overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#161618] text-left"
      >
        {Icon && (
          <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: `${color}22` }}>
            <Icon size={18} weight="bold" style={{ color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && <div className="text-[10px] text-[#71717A]">{subtitle}</div>}
        </div>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

// ---------- main component ----------
const CreativeDashboard = ({ data, currency, onPickProduct, clientId, integrationStatus, month }) => {
  const [filterMarket, setFilterMarket] = useState("all");
  const [filterFunnel, setFilterFunnel] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFormat, setFilterFormat] = useState("all");
  const [filterMapping, setFilterMapping] = useState("all"); // mapped | unmapped | all
  const [minSpend, setMinSpend] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const writeEnabled = (integrationStatus?.facebook?.permission_level === "write_enabled");

  // Flatten all ads into a single array with ancestors + labels
  const { allAds, kpis, productAgg, categories, categoryTotalRevenue, funnel, availableMarkets, availableFunnels, availableFormats } = useMemo(() => {
    if (!data?.campaigns) return { allAds: [], kpis: {}, productAgg: [], categories: [], categoryTotalRevenue: 0, funnel: {}, availableMarkets: [], availableFunnels: [], availableFormats: [] };
    const flat = [];
    const marketSet = new Set();
    const funnelSet = new Set();
    const formatSet = new Set();
    for (const c of data.campaigns) {
      const mf = detectMarketFunnel(c.name || "");
      for (const s of c.ad_sets || []) {
        for (const ad of s.ads || []) {
          const enriched = {
            ...ad,
            _campaign_id: c.id,
            _campaign_name: c.name,
            _adset_id: s.id,
            _adset_name: s.name,
            _market: mf.market,
            _funnel: mf.funnel,
          };
          if (mf.market) marketSet.add(mf.market);
          if (mf.funnel) funnelSet.add(mf.funnel);
          const fmt = (ad.creative || {}).format || ad.creative_type;
          if (fmt && fmt !== "unknown") formatSet.add(fmt);
          flat.push(enriched);
        }
      }
    }

    // Build product revenue aggregation across mapped ads
    const productMap = new Map();
    for (const ad of flat) {
      const prod = (ad.shopify || {}).product;
      if (!prod?.id) continue;
      const key = prod.id;
      const existing = productMap.get(key) || {
        id: prod.id, title: prod.title, handle: prod.handle, image: prod.image,
        ads: [], spend: 0, revenue: 0, orders: 0, units: 0, impressions: 0, clicks: 0,
      };
      existing.ads.push(ad);
      existing.spend += (ad.metrics || {}).spend || 0;
      existing.revenue += (ad.shopify || {}).revenue || 0;
      existing.orders += (ad.shopify || {}).orders || 0;
      existing.units += (ad.shopify || {}).units || 0;
      existing.impressions += (ad.metrics || {}).impressions || 0;
      existing.clicks += (ad.metrics || {}).clicks || 0;
      productMap.set(key, existing);
    }
    const products = Array.from(productMap.values()).map((p) => ({
      ...p,
      roas: p.spend > 0 ? p.revenue / p.spend : 0,
    }));
    products.sort((a, b) => b.revenue - a.revenue);
    // Top 20% products by revenue → product winners
    const top20Count = Math.max(1, Math.ceil(products.length * 0.2));
    const topProductIds = new Set(products.slice(0, top20Count).filter((p) => p.revenue > 0).map((p) => p.id));

    // Label each ad
    for (const ad of flat) {
      ad._labels = labelAd(ad, { topProductIds });
    }

    // KPI totals (from all ads, not filtered)
    const totals = flat.reduce((acc, a) => {
      const m = a.metrics || {}, sh = a.shopify || {};
      acc.spend += m.spend || 0;
      acc.impressions += m.impressions || 0;
      acc.clicks += m.clicks || 0;
      acc.purchases += m.purchases || 0;
      acc.shRevenue += sh.revenue || 0;
      acc.shOrders += sh.orders || 0;
      acc.active += a.effective_status === "ACTIVE" ? 1 : 0;
      const mapped = (sh.product || sh.collection) ? 1 : 0;
      acc.mapped += mapped;
      if (mapped) {
        acc.mappedSpend += m.spend || 0;
        acc.mappedRevenue += sh.revenue || 0;
      } else {
        acc.unmappedSpend += m.spend || 0;
      }
      if ((sh.roas || 0) >= 2 && (m.spend || 0) >= 200) {
        acc.profitableAds += 1;
      }
      if ((m.spend || 0) > 0 || (sh.revenue || 0) > 0) {
        acc.adsWithActivity += 1;
      }
      return acc;
    }, {
      spend: 0, impressions: 0, clicks: 0, purchases: 0,
      shRevenue: 0, shOrders: 0, active: 0, mapped: 0,
      mappedSpend: 0, mappedRevenue: 0, unmappedSpend: 0,
      profitableAds: 0, adsWithActivity: 0,
    });

    // Top 10 contribution by revenue
    const topRevenueAds = flat
      .map((a) => ({ rev: (a.shopify || {}).revenue || 0 }))
      .sort((a, b) => b.rev - a.rev)
      .slice(0, 10)
      .reduce((s, a) => s + a.rev, 0);

    const kpiData = {
      ...totals,
      ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
      cpc: totals.clicks > 0 ? totals.spend / totals.clicks : 0,
      cpm: totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0,
      roas: totals.spend > 0 ? totals.shRevenue / totals.spend : 0,
      aov: totals.shOrders > 0 ? totals.shRevenue / totals.shOrders : 0,
      profitablePct: totals.adsWithActivity > 0 ? (totals.profitableAds / totals.adsWithActivity) * 100 : 0,
      top10Contribution: totals.shRevenue > 0 ? (topRevenueAds / totals.shRevenue) * 100 : 0,
      unmappedPct: flat.length > 0 ? ((flat.length - totals.mapped) / flat.length) * 100 : 0,
      mappedRoas: totals.mappedSpend > 0 ? totals.mappedRevenue / totals.mappedSpend : 0,
    };

    // Category / Collection aggregation
    const categoryMap = new Map();
    for (const p of products) {
      const cat = p.title ? (p.product_type || "Uncategorized") : "Uncategorized";
      // product_type lives on original product — re-pull from the ads' product data
      const firstAdProduct = (p.ads[0]?.shopify || {}).product || {};
      const catName = firstAdProduct.product_type || "Uncategorized";
      const entry = categoryMap.get(catName) || {
        name: catName, products: 0, revenue: 0, orders: 0, units: 0, spend: 0,
      };
      entry.products += 1;
      entry.revenue += p.revenue;
      entry.orders += p.orders;
      entry.units += p.units;
      entry.spend += p.spend;
      categoryMap.set(catName, entry);
    }
    const categories = Array.from(categoryMap.values())
      .map((c) => ({ ...c, roas: c.spend > 0 ? c.revenue / c.spend : 0 }))
      .sort((a, b) => b.revenue - a.revenue);
    const categoryTotalRevenue = categories.reduce((s, c) => s + c.revenue, 0) || 1;

    // Meta funnel across filtered(all for now) ads
    const funnel = {
      impressions: totals.impressions,
      clicks: totals.clicks,
      addToCart: flat.reduce((s, a) => s + ((a.metrics || {}).add_to_cart || 0), 0),
      metaPurchases: totals.purchases,
      shopifyOrders: totals.shOrders,
    };

    return {
      allAds: flat,
      kpis: kpiData,
      productAgg: products,
      categories,
      categoryTotalRevenue,
      funnel,
      availableMarkets: Array.from(marketSet).sort(),
      availableFunnels: Array.from(funnelSet).sort(),
      availableFormats: Array.from(formatSet).sort(),
    };
  }, [data]);

  // Apply filters
  const filteredAds = useMemo(() => {
    return allAds.filter((a) => {
      if (filterMarket !== "all" && a._market !== filterMarket) return false;
      if (filterFunnel !== "all" && a._funnel !== filterFunnel) return false;
      if (filterStatus === "active" && a.effective_status !== "ACTIVE") return false;
      if (filterStatus === "paused" && a.effective_status === "ACTIVE") return false;
      if (filterFormat !== "all") {
        const fmt = (a.creative || {}).format || a.creative_type;
        if (fmt !== filterFormat) return false;
      }
      const hasMap = !!((a.shopify || {}).product || (a.shopify || {}).collection);
      if (filterMapping === "mapped" && !hasMap) return false;
      if (filterMapping === "unmapped" && hasMap) return false;
      if (minSpend > 0 && (a.metrics?.spend || 0) < minSpend) return false;
      return true;
    });
  }, [allAds, filterMarket, filterFunnel, filterStatus, filterFormat, filterMapping, minSpend]);

  // Section buckets from filtered ads
  const sections = useMemo(() => {
    const byLabel = (l) => filteredAds.filter((a) => (a._labels || []).includes(l));
    // "Top performing" = any filtered ad with spend > 0, sorted by ROAS then Revenue. Top 15.
    const topPerforming = filteredAds
      .filter((a) => (a.metrics?.spend || 0) > 0)
      .slice()
      .sort((a, b) => {
        const rA = (a.shopify?.roas || 0), rB = (b.shopify?.roas || 0);
        if (rB !== rA) return rB - rA;
        return (b.shopify?.revenue || 0) - (a.shopify?.revenue || 0);
      })
      .slice(0, 15);
    return {
      topPerforming,
      scaling: byLabel("scaling").sort((a, b) => (b.shopify?.roas || 0) - (a.shopify?.roas || 0)).slice(0, 12),
      monitor: byLabel("monitor").sort((a, b) => (b.metrics?.ctr || 0) - (a.metrics?.ctr || 0)).slice(0, 12),
      needsAttention: byLabel("needs_attention").sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0)).slice(0, 12),
      learning: byLabel("learning").sort((a, b) => (b.metrics?.ctr || 0) - (a.metrics?.ctr || 0)).slice(0, 12),
      creativeWinner: byLabel("creative_winner").sort((a, b) => (b.metrics?.ctr || 0) - (a.metrics?.ctr || 0)).slice(0, 12),
      trackingMissing: byLabel("tracking_missing").sort((a, b) => (b.metrics?.purchases || 0) - (a.metrics?.purchases || 0)).slice(0, 12),
      needsMapping: byLabel("needs_mapping").sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0)).slice(0, 12),
    };
  }, [filteredAds]);

  // Action center recommendations
  const actions = useMemo(() => {
    const items = [];
    sections.needsAttention.slice(0, 5).forEach((a) => items.push({
      kind: "needs_attention", ad: a, reason: `Spent ${fmtMoney(a.metrics?.spend || 0, currency)} · ROAS ${fmtRoas(a.shopify?.roas)} · 0 orders — consider pausing or refreshing the creative`,
    }));
    sections.scaling.slice(0, 5).forEach((a) => items.push({
      kind: "scaling", ad: a, reason: `ROAS ${fmtRoas(a.shopify?.roas)} · ${a.shopify?.orders} orders at ${fmtMoney(a.metrics?.spend || 0, currency)} spend — increase budget`,
    }));
    sections.trackingMissing.slice(0, 5).forEach((a) => items.push({
      kind: "tracking_missing", ad: a, reason: `Meta recorded ${a.metrics?.purchases || 0} purchases but Shopify sees 0 orders — check Pixel / attribution window`,
    }));
    sections.needsMapping.slice(0, 5).forEach((a) => items.push({
      kind: "needs_mapping", ad: a, reason: `Spending ${fmtMoney(a.metrics?.spend || 0, currency)} with no product mapped — tag to unlock revenue attribution`,
    }));
    sections.monitor.slice(0, 3).forEach((a) => items.push({
      kind: "monitor", ad: a, reason: `Strong CTR ${fmtPct(a.metrics?.ctr)} but weak sales — review landing page or pricing`,
    }));
    return items;
  }, [sections, currency]);

  if (!data?.campaigns) return null;

  const metaUrl = (level, id) => {
    if (!data.ad_account_id) return "#";
    const acc = data.ad_account_id.startsWith("act_") ? data.ad_account_id : `act_${data.ad_account_id}`;
    const param = { campaign: "selected_campaign_ids", adset: "selected_adset_ids", ad: "selected_ad_ids" }[level];
    return `https://business.facebook.com/adsmanager/manage/ads?act=${acc}&${param}=${id}`;
  };

  return (
    <div className="space-y-4" data-testid="creative-dashboard">
      {/* KPIs — Row 1: Core commerce */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="Total Spend" value={fmtMoney(kpis.spend, currency)} color="#FFEB00" icon={CurrencyInr} />
        <Kpi label="Shopify Revenue" value={fmtMoney(kpis.shRevenue, currency)} color="#86EFAC" icon={ShoppingBag} />
        <Kpi label="Blended ROAS" value={fmtRoas(kpis.roas)} color={kpis.roas >= 2 ? "#86EFAC" : kpis.roas >= 1 ? "#FCD34D" : "#FF9999"} icon={ChartLineUp} />
        <Kpi label="AOV" value={kpis.aov > 0 ? fmtMoney(kpis.aov, currency) : "-"} icon={CurrencyInr} />
        <Kpi label="Shopify Orders" value={fmtNum(kpis.shOrders)} color="#86EFAC" icon={Package} />
        <Kpi label="Meta Purchases" value={fmtNum(kpis.purchases)} icon={Target} />
      </div>

      {/* KPIs — Row 2: Efficiency + Insight metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="CTR" value={fmtPct(kpis.ctr)} icon={Lightning} />
        <Kpi label="CPC" value={fmtMoney(kpis.cpc, currency)} icon={CurrencyInr} />
        <Kpi label="CPM" value={fmtMoney(kpis.cpm, currency)} icon={CurrencyInr} />
        <Kpi label="Active Ads" value={fmtNum(kpis.active)} sub={`of ${fmtNum(allAds.length)} total`} icon={Eye} />
        <Kpi label="Products Mapped" value={fmtNum(kpis.mapped)} sub={`${fmtNum(allAds.length - kpis.mapped)} need tagging`} icon={Package} />
        <Kpi label="% Profitable Ads" value={`${kpis.profitablePct.toFixed(0)}%`} sub="ROAS ≥ 2x, spend ≥ ₹200" color={kpis.profitablePct >= 30 ? "#86EFAC" : kpis.profitablePct >= 15 ? "#FCD34D" : "#FF9999"} icon={TrendUp} />
      </div>

      {/* KPIs — Row 3: Concentration & coverage insights */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="Top-10 Ads Revenue Share" value={`${kpis.top10Contribution.toFixed(0)}%`} sub={kpis.top10Contribution >= 80 ? "Revenue is highly concentrated" : "Revenue spread across many ads"} icon={ChartLineUp} />
        <Kpi label="Unmapped Ads %" value={`${kpis.unmappedPct.toFixed(0)}%`} sub="Ads without Shopify product tagged" color={kpis.unmappedPct >= 50 ? "#FF9999" : kpis.unmappedPct >= 25 ? "#FCD34D" : "#86EFAC"} icon={Question} />
        <Kpi label="Revenue from Mapped" value={fmtMoney(kpis.mappedRevenue, currency)} sub={`ROAS ${fmtRoas(kpis.mappedRoas)} on mapped ads`} color="#86EFAC" icon={ShoppingBag} />
        <Kpi label="Spend on Unmapped" value={fmtMoney(kpis.unmappedSpend, currency)} sub="Revenue attribution unknown" color={kpis.unmappedSpend > 0 ? "#FCD34D" : undefined} icon={Warning} />
      </div>

      {/* Performance Intelligence Engine — primary actionable dashboard */}
      <PerformanceIntelligencePanel
        clientId={clientId}
        currency={currency}
        integrationStatus={integrationStatus}
        month={month}
      />

      {/* Action Center */}
      {actions.length > 0 && (
        <Section
          title="Action Center"
          subtitle={`${actions.length} recommended actions for this month`}
          icon={Lightning}
          color="#F97316"
          testid="action-center"
        >
          <div className="space-y-1.5">
            {actions.map((item, i) => {
              const meta = LABEL_META[item.kind];
              const Icon = meta.icon;
              return (
                <div
                  key={`${item.kind}-${item.ad.id}-${i}`}
                  className="flex items-start gap-2 p-2 rounded bg-[#0A0A0C] hover:bg-[#161618] transition-colors"
                  data-testid="action-item"
                >
                  <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: `${meta.bg}22` }}>
                    <Icon size={13} weight="bold" style={{ color: meta.bg }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <LabelPill l={item.kind} compact />
                      <span className="text-xs text-[#E4E4E7] truncate">{item.ad.name}</span>
                    </div>
                    <div className="text-[10px] text-[#A1A1AA] mt-0.5">{item.reason}</div>
                  </div>
                  <a
                    href={metaUrl("ad", item.ad.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-[#1877F2] hover:underline shrink-0 px-2"
                  >
                    Open in Meta →
                  </a>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Meta Funnel */}
      {funnel.impressions > 0 && (
        <Section
          title="Meta Funnel"
          subtitle="Impressions → Clicks → Add to Cart → Meta Purchases → Shopify Orders (drop-offs flagged)"
          icon={ChartLineUp} color="#3B82F6"
          testid="funnel-section"
        >
          <FunnelView f={funnel} />
        </Section>
      )}

      {/* Category Analysis */}
      {categories.length > 0 && (
        <Section
          title="Category / Collection Analysis"
          subtitle="Revenue contribution by product category (from Shopify product_type)"
          icon={ShoppingBag} color="#EC4899"
          testid="category-section"
        >
          <div className="space-y-1.5">
            {categories.slice(0, 10).map((c) => {
              const pct = (c.revenue / categoryTotalRevenue) * 100;
              return (
                <div key={c.name} className="flex items-center gap-3 p-2 rounded bg-[#0A0A0C]" data-testid="category-row">
                  <div className="w-32 shrink-0 text-xs font-semibold truncate">{c.name}</div>
                  <div className="flex-1 h-6 bg-[#1F1F22] rounded overflow-hidden relative">
                    <div
                      className="h-full flex items-center px-2 text-[10px] font-semibold"
                      style={{
                        width: `${Math.min(100, pct)}%`,
                        background: "linear-gradient(90deg, #EC4899, #F97316)",
                        color: "#fff",
                      }}
                    >
                      {pct >= 8 ? `${pct.toFixed(1)}%` : ""}
                    </div>
                    {pct < 8 && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">
                        {pct.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div className="w-24 text-right text-xs data-number" style={{ color: "#86EFAC" }}>
                    {fmtMoney(c.revenue, currency)}
                  </div>
                  <div className="w-16 text-right text-[10px] text-[#A1A1AA]">
                    {fmtNum(c.orders)} orders
                  </div>
                  <div className="w-20 text-right text-[10px] data-number" style={{ color: c.roas >= 2 ? "#86EFAC" : c.roas >= 1 ? "#FCD34D" : "#FF9999" }}>
                    {fmtRoas(c.roas)}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Filters */}
      <div className="card-flat p-3 flex flex-wrap gap-2 items-center">
        <span className="text-[10px] text-[#71717A] uppercase">Filters:</span>
        {availableMarkets.length > 0 && (
          <select
            value={filterMarket}
            onChange={(e) => setFilterMarket(e.target.value)}
            className="input-default text-[11px] py-1 px-2"
            data-testid="filter-market"
          >
            <option value="all">All markets</option>
            {availableMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {availableFunnels.length > 0 && (
          <select
            value={filterFunnel}
            onChange={(e) => setFilterFunnel(e.target.value)}
            className="input-default text-[11px] py-1 px-2"
            data-testid="filter-funnel"
          >
            <option value="all">All funnels</option>
            {availableFunnels.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input-default text-[11px] py-1 px-2"
          data-testid="filter-status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="paused">Paused only</option>
        </select>
        {availableFormats.length > 0 && (
          <select
            value={filterFormat}
            onChange={(e) => setFilterFormat(e.target.value)}
            className="input-default text-[11px] py-1 px-2"
            data-testid="filter-format"
          >
            <option value="all">All formats</option>
            {availableFormats.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
          </select>
        )}
        <select
          value={filterMapping}
          onChange={(e) => setFilterMapping(e.target.value)}
          className="input-default text-[11px] py-1 px-2"
          data-testid="filter-mapping"
        >
          <option value="all">All (mapped + unmapped)</option>
          <option value="mapped">Mapped only</option>
          <option value="unmapped">Unmapped only</option>
        </select>
        <div className="flex items-center gap-1 text-[11px]">
          <span className="text-[#71717A]">Min spend:</span>
          <input
            type="number"
            value={minSpend}
            onChange={(e) => setMinSpend(Number(e.target.value) || 0)}
            className="input-default text-[11px] py-1 px-2 w-20"
            data-testid="filter-min-spend"
          />
        </div>
        <div className="ml-auto text-[10px] text-[#71717A]">
          Showing {fmtNum(filteredAds.length)} of {fmtNum(allAds.length)} ads
        </div>
      </div>

      {/* Sections */}
      {/* Top Performing Creatives — always first */}
      <AdSection
        title="⭐ Top Performing Creatives"
        subtitle="Top 15 ads by Shopify ROAS & Revenue (this month)"
        icon={Fire} color="#F59E0B"
        ads={sections.topPerforming}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="🟢 Scaling"
        subtitle="Strong ROAS + consistent orders — safe to increase budget"
        icon={TrendUp} color="#16A34A"
        ads={sections.scaling}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="🟡 Monitor"
        subtitle="Mixed signals — good engagement but sales lagging. Keep watching."
        icon={Eye} color="#EAB308"
        ads={sections.monitor}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="🔴 Needs Attention"
        subtitle="High spend with limited revenue — review creative, audience, or pause"
        icon={Warning} color="#DC2626"
        ads={sections.needsAttention}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="🔵 Learning"
        subtitle="Low spend still — give more data before deciding"
        icon={Lightning} color="#3B82F6"
        ads={sections.learning}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="✨ Creative Winners"
        subtitle="Top-engagement creatives (high CTR × low CPC) — study the hook"
        icon={Sparkle} color="#7C3AED"
        ads={sections.creativeWinner}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="⚠️ Tracking Mismatch"
        subtitle="Meta shows purchases but Shopify shows 0 — investigate Pixel / attribution"
        icon={Warning} color="#F97316"
        ads={sections.trackingMissing}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />
      <AdSection
        title="🏷️ Needs Product Mapping"
        subtitle="Ads spending without a Shopify product tagged — tag to unlock revenue attribution"
        icon={Question} color="#A855F7"
        ads={sections.needsMapping}
        currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct}
        clientId={clientId} onAction={writeEnabled ? setPendingAction : null}
      />

      {/* Action confirmation modal */}
      {pendingAction && (
        <ActionConfirmModal
          action={pendingAction}
          clientId={clientId}
          currency={currency}
          onCancel={() => setPendingAction(null)}
          onSuccess={() => { setPendingAction(null); /* parent should reload */ }}
        />
      )}

      {/* Empty filter state */}
      {filteredAds.length === 0 && allAds.length > 0 && (
        <div className="card-flat p-8 text-center text-xs">
          <div className="text-[#A1A1AA] mb-1 font-semibold">No ads match current filters</div>
          <div className="text-[#71717A]">Try widening the filters (e.g., switch Mapping back to "All" or reset Min Spend).</div>
        </div>
      )}

      {/* Product Performance */}
      {productAgg.length > 0 && (
        <Section
          title="Best Selling Products — Product Performance"
          subtitle={`${productAgg.length} Shopify products mapped to ads`}
          icon={ShoppingBag} color="#0EA5E9"
          testid="product-performance"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {productAgg.slice(0, 12).map((p) => (
              <ProductCard key={p.id} product={p} currency={currency} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

// Ad section renders a grid of creative cards
const AdSection = ({ title, subtitle, icon, color, ads, currency, metaUrl, onPickProduct, clientId, onAction }) => {
  if (!ads || ads.length === 0) return null;
  return (
    <Section title={`${title} (${ads.length})`} subtitle={subtitle} icon={icon} color={color}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ads.map((ad) => (
          <CreativeCard key={ad.id} ad={ad} currency={currency} metaUrl={metaUrl} onPickProduct={onPickProduct} clientId={clientId} onAction={onAction} />
        ))}
      </div>
    </Section>
  );
};

const ProductCard = ({ product, currency }) => {
  const bestAd = product.ads.slice().sort((a, b) => (b.shopify?.roas || 0) - (a.shopify?.roas || 0))[0];
  const weakAd = product.ads.slice().sort((a, b) => {
    const sa = (a.metrics?.spend || 0) - (a.shopify?.revenue || 0);
    const sb = (b.metrics?.spend || 0) - (b.shopify?.revenue || 0);
    return sb - sa;
  })[0];
  const ctr = product.impressions > 0 ? (product.clicks / product.impressions) * 100 : 0;

  return (
    <div className="card-flat p-3 flex gap-3" data-testid="product-card">
      {product.image ? (
        <img src={product.image} alt="" className="w-20 h-20 rounded object-cover shrink-0" />
      ) : (
        <div className="w-20 h-20 rounded bg-[#1F1F22] flex items-center justify-center shrink-0">
          <Package size={24} className="text-[#52525B]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-[#E4E4E7] line-clamp-1" title={product.title}>{product.title}</div>
        <div className="text-[9px] text-[#52525B] line-clamp-1">/{product.handle}</div>

        <div className="grid grid-cols-2 gap-1 mt-2 text-[10px]">
          <div><span className="text-[#71717A]">Spend:</span> <span className="data-number" style={{ color: "#FFEB00" }}>{fmtMoney(product.spend, currency)}</span></div>
          <div><span className="text-[#71717A]">Revenue:</span> <span className="data-number" style={{ color: "#86EFAC" }}>{fmtMoney(product.revenue, currency)}</span></div>
          <div><span className="text-[#71717A]">Orders:</span> <span className="data-number">{fmtNum(product.orders)}</span></div>
          <div><span className="text-[#71717A]">ROAS:</span> <span className="data-number" style={{ color: product.roas >= 2 ? "#86EFAC" : product.roas >= 1 ? "#FCD34D" : "#FF9999" }}>{fmtRoas(product.roas)}</span></div>
          <div><span className="text-[#71717A]">Ads:</span> <span className="data-number">{product.ads.length}</span></div>
          <div><span className="text-[#71717A]">CTR:</span> <span className="data-number">{fmtPct(ctr)}</span></div>
        </div>

        {bestAd && (
          <div className="mt-2 text-[10px] text-[#10B981] truncate" title={bestAd.name}>
            🏆 Best ad: <span className="text-[#86EFAC]">{bestAd.name}</span> ({fmtRoas(bestAd.shopify?.roas)})
          </div>
        )}
        {weakAd && weakAd.id !== bestAd?.id && (weakAd.metrics?.spend || 0) > 0 && (
          <div className="text-[10px] text-[#FF6666] truncate" title={weakAd.name}>
            ⚠️ Weak ad: <span>{weakAd.name}</span> (spent {fmtMoney(weakAd.metrics?.spend, currency)})
          </div>
        )}
      </div>
    </div>
  );
};

const FunnelView = ({ f }) => {
  const steps = [
    { label: "Impressions", value: f.impressions, color: "#3B82F6" },
    { label: "Clicks", value: f.clicks, color: "#8B5CF6" },
    { label: "Add to Cart", value: f.addToCart, color: "#EC4899" },
    { label: "Meta Purchases", value: f.metaPurchases, color: "#F97316" },
    { label: "Shopify Orders", value: f.shopifyOrders, color: "#10B981" },
  ];
  const max = f.impressions || 1;

  // Compute drop-offs
  const drops = [];
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1].value;
    const curr = steps[i].value;
    const pct = prev > 0 ? ((prev - curr) / prev) * 100 : 0;
    drops.push({ from: steps[i - 1].label, to: steps[i].label, pct, isMetaVsShop: i === 4 });
  }
  // Biggest drop among first 3 stages (not the Meta vs Shopify one, which is tracking, not funnel)
  const biggest = drops.slice(0, 3).filter(d => d.pct > 0).sort((a, b) => b.pct - a.pct)[0];
  // Tracking flag
  let trackingFlag = null;
  if (f.addToCart === 0 && f.clicks > 100) {
    trackingFlag = "⚠️ 0 Add-to-Cart events tracked — Pixel 'AddToCart' event may not be firing on the site.";
  }

  return (
    <div className="space-y-2">
      {biggest && (
        <div className="rounded px-3 py-2 text-xs" style={{ background: "#DC262622", color: "#FF9999" }}>
          <strong>Biggest drop-off:</strong> {biggest.from} → {biggest.to} ({biggest.pct.toFixed(1)}% lost).
          {biggest.from === "Impressions" && " Likely targeting or creative hook issue."}
          {biggest.from === "Clicks" && " Landing page load / product page not converting."}
          {biggest.from === "Add to Cart" && " Checkout friction — price, shipping, or trust issue."}
        </div>
      )}
      {trackingFlag && (
        <div className="rounded px-3 py-2 text-xs" style={{ background: "#F9731622", color: "#FCD34D" }}>
          {trackingFlag}
        </div>
      )}
      {steps.map((s, i) => {
        const pct = (s.value / max) * 100;
        const prev = i > 0 ? steps[i - 1].value : null;
        const dropOff = prev && prev > 0 ? ((prev - s.value) / prev) * 100 : null;
        return (
          <div key={s.label} className="flex items-center gap-3">
            <div className="w-32 text-xs font-semibold shrink-0">{s.label}</div>
            <div className="flex-1 h-8 bg-[#1F1F22] rounded overflow-hidden relative">
              <div
                className="h-full flex items-center px-3 text-[11px] font-semibold"
                style={{ width: `${Math.max(2, pct)}%`, background: s.color, color: "#fff" }}
              >
                {fmtNum(s.value)}
              </div>
            </div>
            <div className="w-24 text-right text-[10px] text-[#A1A1AA]">
              {prev !== null && prev > 0 ? `${dropOff.toFixed(0)}% drop` : ""}
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-[#27272A]">
        <div className="text-[10px]">
          <div className="text-[#71717A]">CTR (Click-through)</div>
          <div className="data-number text-sm">{f.impressions > 0 ? ((f.clicks / f.impressions) * 100).toFixed(2) : "0"}%</div>
        </div>
        <div className="text-[10px]">
          <div className="text-[#71717A]">Click → ATC</div>
          <div className="data-number text-sm">{f.clicks > 0 ? ((f.addToCart / f.clicks) * 100).toFixed(2) : "0"}%</div>
        </div>
        <div className="text-[10px]">
          <div className="text-[#71717A]">ATC → Purchase (Meta)</div>
          <div className="data-number text-sm">{f.addToCart > 0 ? ((f.metaPurchases / f.addToCart) * 100).toFixed(2) : "0"}%</div>
        </div>
        <div className="text-[10px]">
          <div className="text-[#71717A]">Meta vs Shopify Match</div>
          <div className="data-number text-sm">{f.metaPurchases > 0 ? ((f.shopifyOrders / f.metaPurchases) * 100).toFixed(0) : "0"}%</div>
        </div>
      </div>
    </div>
  );
};

const ActionConfirmModal = ({ action, clientId, currency, onCancel, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [pct, setPct] = useState(20);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [userEmail] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("rios_user") || "{}");
      return u.email || "unknown";
    } catch { return "unknown"; }
  });

  const titleFor = {
    pause_ad: "Pause Ad on Meta",
    resume_ad: "Resume Ad on Meta",
    adjust_adset_budget: "Adjust Ad Set Budget",
  }[action.kind];

  const submit = async () => {
    if (reason.trim().length < 5) {
      setError("Please provide a meaningful reason (≥ 5 chars)");
      return;
    }
    setError(null); setSubmitting(true);
    try {
      const body = {
        action: action.kind,
        entity_id: action.entity_id,
        reason: reason.trim(),
        user_email: userEmail,
      };
      if (action.kind === "adjust_adset_budget") body.budget_change_pct = pct;
      const res = await axios.post(`${API}/clients/${clientId}/facebook/ad-action`, body, { timeout: 30000 });
      setResult(res.data);
      setTimeout(() => { onSuccess(); }, 1200);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onCancel}>
      <div className="bg-[#0F0F11] border border-[#27272A] rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="action-confirm-modal">
        <div className="p-4 border-b border-[#27272A]">
          <h4 className="text-sm font-semibold">{titleFor}</h4>
          <p className="text-[11px] text-[#A1A1AA] mt-1 truncate" title={action.entity_name}>
            {action.level === "ad" ? "Ad" : "Ad Set"}: <span className="text-[#E4E4E7]">{action.entity_name}</span>
          </p>
        </div>
        <div className="p-4 space-y-3">
          {action.kind === "adjust_adset_budget" && (
            <div>
              <label className="text-[10px] uppercase text-[#71717A]">Budget change</label>
              <div className="flex gap-2 mt-1">
                {[20, 50, 100, -20, -50].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPct(p)}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${pct === p ? "bg-[#FFEB00] text-black" : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"}`}
                    data-testid={`budget-pct-${p}`}
                  >
                    {p > 0 ? `+${p}%` : `${p}%`}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase text-[#71717A]">Reason (required, ≥ 5 chars) — saved to audit log</label>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. ROAS 0.2x, 0 orders for 5 days. Pausing to review creative."
              className="input-default text-xs py-2 px-3 w-full mt-1"
              data-testid="action-reason-input"
            />
          </div>
          <div className="text-[10px] text-[#71717A]">Action will be logged: user <span className="text-[#A1A1AA]">{userEmail}</span> · timestamp · before/after Meta values</div>
          {error && <div className="text-xs p-2 rounded" style={{ background: "#FF444422", color: "#FF6666" }}>{error}</div>}
          {result && (
            <div className="text-xs p-2 rounded" style={{ background: "#10B98122", color: "#6EE7B7" }}>
              ✓ Done. Meta accepted the change.
            </div>
          )}
        </div>
        <div className="p-3 border-t border-[#27272A] flex justify-end gap-2">
          <button onClick={onCancel} disabled={submitting} className="px-3 py-1.5 rounded text-xs bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !!result}
            className="px-3 py-1.5 rounded text-xs font-semibold"
            style={{ background: action.kind === "pause_ad" ? "#DC2626" : action.kind === "resume_ad" ? "#16A34A" : "#F59E0B", color: "#fff" }}
            data-testid="action-confirm-btn"
          >
            {submitting ? "Sending to Meta…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreativeDashboard;
